import type { MaterialId } from '../core/types';
import type { NodeLevels } from '../core/skilltree';

export type { MaterialId };

/**
 * Les six **Matériaux** de dés — révision du GD du 15/10 (`docs/INFINI_PROPOSITION.md`
 * §5) et arbitrage **A94** (réponses 4, 14 et 16).
 *
 * Seules les *Corrupted GachaDice* donnent un Matériau (A94-4) ; le *Trucage*
 * le change. Boisé, Osseux et Ferreux (`tier: 1`) sont tirés dès *Artisan des
 * dés* ; Caoutchouc, Silicium et Du Vide (`tier: 2`) seulement avec
 * *Matériologie* (A94-14).
 *
 * v0.46.0 : les bonus à 3 et 5 pièces n'étaient que des textes ; v0.47.0
 * (A95) : leurs effets sont dans `core/materials.ts`, `core/combat.ts` et
 * `core/game.ts`. Ces textes restent ceux de l'interface.
 */
export interface MaterialDef {
  id: MaterialId;
  name: string;
  tier: 1 | 2;
  /** Bonus à 3 pièces (nœud *Matériau*), en clair. */
  bonus3: string;
  /** Bonus à 5 pièces (nœud *Matériologie*), en clair. */
  bonus5: string;
}

export const MATERIALS: MaterialDef[] = [
  {
    id: 'wood',
    name: 'Boisé',
    tier: 1,
    // A94-16 : une fois par Invasion.
    bonus3:
      'Une fois par Invasion, si le compteur descend sous 5 secondes, 10 secondes lui sont ajoutées.',
    bonus5: 'Le contenant inflige le maximum de dégâts entre le lancer précédent et le lancer actuel.',
  },
  {
    id: 'bone',
    name: 'Osseux',
    tier: 1,
    bonus3:
      'Les sorts d’emplacement brisent la résistance des monstres. Contre un monstre brisé, ' +
      'les dégâts du lancer sont multipliés par 1,5.',
    // A94-14.
    bonus5:
      'Briser un monstre déjà brisé ajoute +0,5 au multiplicateur ; un monstre reste brisé ' +
      'jusqu’à sa mort.',
  },
  {
    id: 'iron',
    name: 'Ferreux',
    tier: 1,
    bonus3: 'Les dés du contenant deviennent plus lourds : multiplicateur de 1,25.',
    bonus5:
      'Par face maximale obtenue avec vos dés Maîtres, vos dés Basiques infligent 2 fois plus ' +
      'de dégâts (1 face maximale : ×2, 2 faces maximales : ×4).',
  },
  {
    id: 'rubber',
    name: 'Caoutchouc',
    tier: 2,
    // A94-16 : les deux faces du rebond comptent aussi dans les dégâts.
    bonus3:
      'Le dé de votre premier emplacement rebondit : ses deux faces comptent pour les sorts et ' +
      'les dégâts.',
    bonus5: 'Les dés de vos emplacements 2 et 3 rebondissent également.',
  },
  {
    id: 'silicon',
    name: 'Silicium',
    tier: 2,
    bonus3:
      'Si le lancer tue le monstre, les dégâts excédentaires sont ajoutés au lancer suivant.',
    // A122-6 : Électrification (remplace le dé fantôme d'A94-16).
    bonus5:
      'Chaque lancer effectué vous fait gagner un cumul d’Électrification. Chaque cumul vous ' +
      'donne 1 % de dégâts supplémentaires, cumulable 50 fois.',
  },
  {
    id: 'void',
    name: 'Du Vide',
    tier: 2,
    bonus3: 'Annule les effets du monstre.',
    bonus5: 'Le lancer inflige le maximum de dégâts entre le lancer précédent et le lancer actuel.',
  },
];

export const MATERIAL_BY_ID: Record<MaterialId, MaterialDef> = Object.fromEntries(
  MATERIALS.map((m) => [m.id, m]),
) as Record<MaterialId, MaterialDef>;

/** Identifiant de Matériau connu ? (relecture de sauvegarde, forgeur de debug) */
export function isMaterialId(v: unknown): v is MaterialId {
  return typeof v === 'string' && Object.prototype.hasOwnProperty.call(MATERIAL_BY_ID, v);
}

/**
 * Matériaux tirables aux *Corrupted GachaDice* (A94-14) : les trois de rang 1
 * toujours, les trois de rang 2 avec *Matériologie* (`aby_materiology`).
 */
export function availableMaterials(levels: NodeLevels): MaterialId[] {
  const materiology = (levels.aby_materiology ?? 0) >= 1;
  return MATERIALS.filter((m) => m.tier === 1 || materiology).map((m) => m.id);
}
